import {
  useState,
  useEffect,
  ChangeEvent,
  FormEvent,
  ReactElement,
} from 'react'
import axios from 'axios'
import * as Styled from './Wallet.styled'
import { WALLET_INIT_CARD_API } from '../../utils/API'
import { getLocalStorage } from '../../utils/localStorage'
import { randomUuid } from '../../utils/uuid'

// Omise.js global injected by CDN load
declare global {
  interface Window {
    Omise: {
      setPublicKey: (k: string) => void
      createToken: (
        type: 'card',
        card: OmiseCardPayload,
        cb: (statusCode: number, response: OmiseCardResponse) => void
      ) => void
    }
  }
}

interface OmiseCardPayload {
  number: string
  name: string
  expiration_month: number
  expiration_year: number
  security_code: string
}

interface OmiseCardResponse {
  id?: string
  message?: string
  code?: string
}

interface InitCardResponse {
  ok?: boolean
  topup_id: string
  checkout_url?: string | null
  replayed?: boolean
}

const OMISE_PUBLIC_KEY: string = process.env.REACT_APP_OMISE_PUBLIC_KEY || ''
const OMISE_SCRIPT_SRC = 'https://cdn.omise.co/omise.js'

// Module-level cache — once Omise.js is loaded into the page it stays for the
// session. Useful when the user opens/closes CardForm multiple times.
let scriptLoaded = false

function loadOmiseScript(): Promise<void> {
  if (scriptLoaded || typeof window === 'undefined') return Promise.resolve()
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src*="${OMISE_SCRIPT_SRC}"]`)
    if (existing) {
      scriptLoaded = true
      return resolve()
    }
    const s = document.createElement('script')
    s.src = OMISE_SCRIPT_SRC
    s.onload = (): void => {
      scriptLoaded = true
      resolve()
    }
    s.onerror = (): void => reject(new Error('Failed to load payment library'))
    document.head.appendChild(s)
  })
}

// Group digits like the physical card face: "4242 4242 4242 4242".
// Submit strips spaces before tokenize, so formatting is display-only.
function formatCardNumber(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 19)
  return digits.replace(/(\d{4})(?=\d)/g, '$1 ')
}

// Auto-insert the "/" so the user only types digits (MMYY → MM/YY).
// "1/" is padded to "01/" so a single-digit month typed with a slash
// doesn't shift into the year ("1/25" → "01/25", not "12/5").
function formatExpiry(raw: string): string {
  const padded = raw.replace(/^(\d)\//, '0$1/')
  const digits = padded.replace(/\D/g, '').slice(0, 4)
  if (digits.length < 2) return digits
  if (digits.length === 2) return padded.endsWith('/') ? `${digits}/` : digits
  return `${digits.slice(0, 2)}/${digits.slice(2)}`
}

interface Props {
  amount: number
  // Card surcharge (display-only — credit server computes the real fee).
  fee: number
  // No onSuccess callback — checkout_url always redirects (3DS or non-3DS).
  // If a future server build returns no checkout_url, we synthesize a redirect
  // to the same URL with topup=pending so the parent's polling effect picks
  // it up.
  onCancel: () => void
}

export default function CardForm({ amount, fee, onCancel }: Props): ReactElement {
  const [number, setNumber] = useState<string>('')
  const [expiry, setExpiry] = useState<string>('')
  const [cvv, setCvv] = useState<string>('')
  const [name, setName] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  // idempotency_key minted ONCE per CardForm mount. Retries (e.g. fix CVV,
  // re-submit) reuse the same key → server replays the existing pending row
  // instead of creating a second Omise charge. Bypassing this (mint inside
  // submit) was the central blocker fixed in credit a92c63d.
  const [idemKey] = useState<string>(() => randomUuid())

  useEffect(() => {
    loadOmiseScript().catch((e: Error) => setError(e.message))
  }, [])

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault()
    // Double-tap guard FIRST — before validation. A second tap during the gap
    // between validation success and setLoading would otherwise re-enter and
    // tokenize a second time. Reviewer M2 (Phase 5).
    if (loading) return
    setLoading(true)
    setError(null)

    // Reviewer M3 fix: literal placeholder "pkey_REPLACE_BEFORE_DEPLOY" passes
    // truthy check → would reach window.Omise.setPublicKey() and crash with a
    // cryptic Omise error. Belt-and-braces: reject any key containing "REPLACE"
    // so a forgotten env replace fails fast with the friendly UI message.
    if (!OMISE_PUBLIC_KEY || OMISE_PUBLIC_KEY.includes('REPLACE')) {
      setError('Card payments are currently unavailable')
      setLoading(false)
      return
    }

    // Parse expiry MM/YY
    const m = expiry.match(/^(\d{2})\s*\/\s*(\d{2})$/)
    if (!m) {
      setError('Invalid expiry format (MM/YY)')
      setLoading(false)
      return
    }
    const expMonth = parseInt(m[1], 10)
    const expYear = 2000 + parseInt(m[2], 10)
    if (expMonth < 1 || expMonth > 12) {
      setError('Invalid expiry month')
      setLoading(false)
      return
    }
    if (expYear < new Date().getFullYear()) {
      setError('Card has expired')
      setLoading(false)
      return
    }

    try {
      // Tokenize via Omise.js
      window.Omise.setPublicKey(OMISE_PUBLIC_KEY)
      const token: string = await new Promise<string>((resolve, reject) => {
        window.Omise.createToken(
          'card',
          {
            number: number.replace(/\s/g, ''),
            name,
            expiration_month: expMonth,
            expiration_year: expYear,
            security_code: cvv,
          },
          (statusCode: number, response: OmiseCardResponse) => {
            if (statusCode === 200 && response.id) {
              resolve(response.id)
            } else {
              reject(
                new Error(
                  response.message ?? response.code ?? 'tokenize failed'
                )
              )
            }
          }
        )
      })

      // Submit to service (which forwards HMAC to credit)
      const local = getLocalStorage()
      const res = await axios.post<InitCardResponse>(
        WALLET_INIT_CARD_API,
        {
          amount,
          omise_token: token,
          idempotency_key: idemKey,
        },
        {
          headers: { Authorization: `Bearer ${local.login.token}` },
        }
      )

      const { topup_id, checkout_url } = res.data
      if (checkout_url) {
        // 3DS path — Omise redirects user away, then back to
        // remedy.coffee/wallet?topup=pending&id=<topup_id>
        window.location.href = checkout_url
        return
      }

      // Non-3DS path (rare) — server already verified. Redirect to /wallet
      // with the same pending query so the parent's polling effect handles
      // success UI uniformly.
      window.location.href = `/wallet?topup=pending&id=${encodeURIComponent(
        topup_id
      )}`
    } catch (err) {
      // Service errorHandler wraps as {error:{status,code,message,name}} —
      // data.error is an OBJECT. Rendering it crashed React to a white screen
      // on the first live web card topup (2026-06-11). Extract .message only.
      const axiosErr = err as {
        response?: {
          data?: { message?: string; error?: { message?: string } | string }
        }
        message?: string
      }
      const dataErr = axiosErr?.response?.data?.error
      setError(
        (typeof dataErr === 'object' ? dataErr?.message : dataErr) ??
          axiosErr?.response?.data?.message ??
          axiosErr?.message ??
          'Something went wrong'
      )
      setLoading(false)
    }
  }

  return (
    <Styled.ModalOverlay onClick={loading ? undefined : onCancel}>
      <Styled.ModalCard onClick={(e) => e.stopPropagation()}>
        <Styled.ModalTitle>Pay by Credit / Debit Card</Styled.ModalTitle>

        <Styled.AmountDisplay>
          <Styled.AmountDisplayLabel>Top-Up Amount</Styled.AmountDisplayLabel>
          <Styled.AmountDisplayValue>
            ฿{amount.toLocaleString()}
          </Styled.AmountDisplayValue>
          {fee > 0 && (
            <div style={{ fontSize: 13, color: '#57534e', marginTop: 6 }}>
              Card fee ฿{fee.toLocaleString()} · Total charge{' '}
              <strong>฿{(amount + fee).toLocaleString()}</strong>
            </div>
          )}
        </Styled.AmountDisplay>

        <form onSubmit={submit}>
          <Styled.FormInput
            type='text'
            inputMode='numeric'
            autoComplete='cc-number'
            placeholder='Card number'
            required
            maxLength={23}
            value={number}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setNumber(formatCardNumber(e.target.value))
            }
          />

          <Styled.FormRow>
            <Styled.FormInputFlex
              type='text'
              inputMode='numeric'
              autoComplete='cc-exp'
              placeholder='MM/YY'
              required
              maxLength={5}
              value={expiry}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setExpiry(formatExpiry(e.target.value))
              }
            />
            <Styled.FormInputCvv
              type='text'
              inputMode='numeric'
              autoComplete='cc-csc'
              placeholder='CVV'
              required
              maxLength={4}
              value={cvv}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setCvv(e.target.value)
              }
            />
          </Styled.FormRow>

          <Styled.FormInput
            type='text'
            autoComplete='cc-name'
            placeholder='Name on card'
            required
            value={name}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setName(e.target.value)
            }
          />

          {error && <Styled.FormError>{error}</Styled.FormError>}

          <Styled.SubmitBtn type='submit' disabled={loading}>
            {loading
              ? 'Processing…'
              : `Pay ฿${(amount + fee).toLocaleString()}`}
          </Styled.SubmitBtn>

          <Styled.ModalCloseBtn
            type='button'
            disabled={loading}
            onClick={loading ? undefined : onCancel}
          >
            Cancel
          </Styled.ModalCloseBtn>
        </form>
      </Styled.ModalCard>
    </Styled.ModalOverlay>
  )
}
